import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Copy, Globe, Loader2, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { useOrgs } from "@/lib/orgs";
import { cn } from "@/lib/utils";
import { copyText } from "@/lib/clipboard";
import { useParams } from "react-router-dom";

/**
 * Org-level Domains page — global admin view of every apex domain across
 * the org's projects. Per-project add/manage now lives on each project's
 * Domains tab (`/orgs/:org/projects/:project/domains`), which surfaces
 * the v3 (003-custom-domain-wizard) wizard. This page stays for
 * admin-wide listing + wildcard-cert management.
 */
interface DomainRow {
  id: string;
  apex: string;
  wildcardEnabled: boolean;
  createdAt: string;
}

interface DomainStatus {
  apex: string;
  wildcardEnabled: boolean;
  dnsResolved: boolean;
  expectedTarget: string;
  routes: Array<{ id: string; hostname: string; target: string; certIssued: boolean }>;
}

export function OrgDomains() {
  const orgs = useOrgs();
  const params = useParams<{ org?: string }>();
  const orgSlug = params.org ?? orgs.data?.[0]?.slug;
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const domains = useQuery({
    enabled: !!orgSlug,
    queryKey: ["domains", orgSlug],
    queryFn: async () => {
      const r = await api.get<{ domains: DomainRow[] }>(`/orgs/${orgSlug}/domains`);
      return r.domains;
    },
  });

  if (!orgSlug) return null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" /> Domains
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Custom hostnames for projects in <span className="font-mono">{orgSlug}</span>
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button variant="primary"><Plus className="h-3.5 w-3.5" /> Add domain</Button>
          </DialogTrigger>
          <AddDomainDialog
            orgSlug={orgSlug}
            onDone={() => {
              setAddOpen(false);
              qc.invalidateQueries({ queryKey: ["domains", orgSlug] });
            }}
          />
        </Dialog>
      </div>

      {domains.isLoading && <div className="of-card px-4 py-10 text-center text-sm text-muted-foreground">Loading domains…</div>}
      {domains.data?.length === 0 && (
        <div className="of-card px-6 py-16 text-center">
          <Globe className="h-8 w-8 text-muted-foreground/40 mx-auto" />
          <div className="text-sm font-semibold mt-3">No custom domains yet</div>
          <div className="text-xs text-muted-foreground mt-1">
            Add a domain you own, point DNS, and we'll issue HTTPS automatically.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {domains.data?.map((d) => (
          <DomainCard key={d.id} orgSlug={orgSlug} domain={d} />
        ))}
      </div>
    </div>
  );
}

function DomainCard({ orgSlug, domain }: { orgSlug: string; domain: DomainRow }) {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["domain-status", domain.id],
    queryFn: () => api.get<DomainStatus>(`/orgs/${orgSlug}/domains/${domain.id}/status`),
    refetchInterval: 15_000,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/orgs/${orgSlug}/domains/${domain.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains", orgSlug] }),
  });
  const s = status.data;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <CardTitle className="font-mono truncate">{domain.apex}</CardTitle>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {domain.wildcardEnabled && (
            <span className="text-2xs font-mono uppercase tracking-wider bg-accent-subtle text-accent border border-accent/30 px-1.5 py-0.5 rounded-sm">
              wildcard
            </span>
          )}
          <Button variant="ghost" size="icon" onClick={() => remove.mutate()} title="Delete domain">
            <Trash2 className="h-3.5 w-3.5 text-danger" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Row
          label="DNS"
          ok={s?.dnsResolved ?? false}
          okText="Resolves to edge"
          waitText="Awaiting DNS — point at"
          mono={s?.expectedTarget}
        />
        <div>
          <div className="text-2xs text-subtle-foreground uppercase tracking-wider mb-1">Routes</div>
          {(s?.routes.length ?? 0) === 0 ? (
            <div className="text-xs text-muted-foreground italic">No routes registered yet. Deploy a project to populate.</div>
          ) : (
            <ul className="space-y-1">
              {s?.routes.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-xs">
                  <span className={cn("inline-block h-1.5 w-1.5 rounded-full", r.certIssued ? "bg-success" : "bg-attention animate-pulse-dot")} />
                  <span className="font-mono truncate flex-1">{r.hostname}</span>
                  <span className="text-2xs text-subtle-foreground font-mono">{r.target}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, ok, okText, waitText, mono }: { label: string; ok: boolean; okText: string; waitText: string; mono?: string }) {
  return (
    <div className="text-xs flex items-start gap-2">
      <div className={cn("h-4 w-4 rounded-full grid place-items-center shrink-0", ok ? "bg-success-subtle text-success" : "bg-attention-subtle text-attention")}>
        {ok ? <Check className="h-3 w-3" /> : <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
      <div className="min-w-0">
        <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</div>
        <div className="text-foreground">
          {ok ? okText : waitText}
          {mono && <span className="font-mono ml-1">{mono}</span>}
        </div>
      </div>
    </div>
  );
}

function AddDomainDialog({ orgSlug, onDone }: { orgSlug: string; onDone: () => void }) {
  const [apex, setApex] = useState("");
  const [wildcard, setWildcard] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<null | {
    apex: { type: string; host: string; value: string; hint: string };
    sub: { type: string; host: string; value: string; hint: string };
  }>(null);

  const m = useMutation({
    mutationFn: () =>
      api.post<{ domain: DomainRow; instructions: { apex: { type: string; host: string; value: string; hint: string }; sub: { type: string; host: string; value: string; hint: string } } }>(`/orgs/${orgSlug}/domains`, {
        apex,
        wildcardEnabled: wildcard,
      }),
    onSuccess: (r) => setInstructions(r.instructions),
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-success" /> Add a domain
        </DialogTitle>
        <DialogDescription>
          We never issue a certificate for a domain that isn't claimed in here — squatting is impossible.
        </DialogDescription>
      </DialogHeader>

      {!instructions ? (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="apex">Apex domain</Label>
            <Input id="apex" placeholder="acme.com" value={apex} onChange={(e) => setApex(e.target.value.toLowerCase())} autoFocus />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={wildcard}
              onChange={(e) => setWildcard(e.target.checked)}
              className="h-3.5 w-3.5 mt-0.5 accent-[rgb(var(--of-accent-emphasis))]"
            />
            <div className="space-y-0.5">
              <div className="text-sm font-medium">Enable wildcard subdomains for branch previews</div>
              <div className="text-2xs text-subtle-foreground">
                Adds <span className="font-mono">*.{apex || "acme.com"}</span> so branch URLs like
                <span className="font-mono"> staging.{apex || "acme.com"}</span> work without per-branch DNS.
              </div>
            </div>
          </label>
          {err && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{err}</div>}
          <DialogFooter>
            <Button variant="primary" disabled={!apex || m.isPending} onClick={() => m.mutate()}>
              {m.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Add domain
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm">
            <CheckCircle2 className="h-4 w-4 text-success inline-block mr-1 -mt-0.5" />
            Created. Add these DNS records at your registrar:
          </div>
          <DnsRow record={instructions.apex} />
          {wildcard && <DnsRow record={instructions.sub} />}
          <div className="text-2xs text-subtle-foreground">
            Once DNS propagates, the first browser visit triggers HTTPS issuance automatically. No certificates to copy.
          </div>
          <DialogFooter>
            <Button variant="primary" onClick={onDone}>Done</Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  );
}

function DnsRow({ record }: { record: { type: string; host: string; value: string; hint: string } }) {
  const [copied, setCopied] = useState<"host" | "value" | null>(null);
  return (
    <div className="of-mono-surface border border-border rounded-md p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono uppercase tracking-wider text-2xs bg-subtle text-foreground border border-border rounded px-1 py-px">
          {record.type}
        </span>
        <CopyChip
          label="Host"
          value={record.host}
          onCopy={() => setCopied("host")}
          copied={copied === "host"}
        />
        <CopyChip
          label="Value"
          value={record.value}
          onCopy={() => setCopied("value")}
          copied={copied === "value"}
        />
      </div>
      <div className="text-2xs text-subtle-foreground">{record.hint}</div>
    </div>
  );
}

function CopyChip({ label, value, onCopy, copied }: { label: string; value: string; onCopy: () => void; copied: boolean }) {
  return (
    <button
      onClick={async () => {
        await copyText(value);
        onCopy();
      }}
      className="inline-flex items-center gap-1 max-w-full min-w-0 font-mono text-2xs px-1 py-0.5 rounded hover:bg-subtle transition-colors"
      title={`Copy ${label}`}
    >
      <span className="text-subtle-foreground shrink-0">{label}:</span>
      <span className="text-foreground truncate">{value}</span>
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3 text-subtle-foreground opacity-60" />}
    </button>
  );
}

// Re-export icon to silence lint about unused
export const _ = X;
